/**
 * The responsibility of this file is to define the styles for the MUI components used in the application.
 * This is done by using the createTheme function from MUI and passing in the styles for each component.
 * Wrap this style with the theme provider in the main index.js file or main root component of the application.
 * This will ensure that the styles are applied to all instances of the component in the application.
 */

import { createTheme } from '@mui/material';
import { palette } from './palette';

export const theme = createTheme({
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    minWidth: '250px',
                    maxWidth: '100%',
                    backgroundColor: 'transparent',
                    borderRadius: '1rem',
                    borderColor: 'var(--quaternary-color)',
                    color: 'var(--quaternary-color)',
                    fontSize: '1.125rem',
                    '&.Mui-error': {
                        borderColor: 'var(--error-main-color)',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--error-main-color)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--error-main-color)'
                        }
                    },
                    '&.Mui-focused': {
                        backgroundColor: 'transparent'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--quaternary-color)'
                    },
                    '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--secondary-color)'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--secondary-color)'
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--quaternary-color)',
                        color: 'var(--quaternary-color)',
                        opacity: '0.5'
                    },
                    '&[readonly] .MuiOutlinedInput-notchedOutline, & input[readonly] + .MuiOutlinedInput-notchedOutline':
                    {
                        borderColor: 'var(--quaternary-color)'
                    }
                },
                input: {
                    fontFamily: 'var(--primary-heading-font-family)',
                    '&.Mui-disabled': {
                        WebkitTextFillColor: 'var(--quaternary-color)',
                        opacity: '0.5'
                    }
                }
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: 'var(--quaternary-color)',
                    fontWeight: '300',
                    fontFamily: 'var(--primary-heading-font-family)',
                    fontSize: '1.125rem',
                    '&.Mui-focused': {
                        color: 'var(--quaternary-color)' // when label is shrinked
                    },
                    '&.Mui-error': {
                        color: 'var(--error-main-color)'
                    },
                    '&.Mui-disabled': {
                        color: 'var(--quaternary-color)',
                        opacity: '0.5'
                    }
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    color: 'var(--quaternary-color)',
                    fontWeight: '300',
                    margin: '6px 0px 0px 3px',
                    fontFamily: 'var(--primary-heading-font-family)',
                    fontSize: '0.875rem',
                    lineHeight: 'none',
                    letterSpacing: '0px',
                    '&.Mui-error': {
                        color: 'var(--error-main-color)'
                    },
                    '&.Mui-disabled': {
                        color: 'var(--quaternary-color)',
                        opacity: '0.5'
                    }
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        '& .MuiAutocomplete-inputRoot': {
                            backgroundColor: 'transparent',
                            '&.Mui-focused': {
                                backgroundColor: 'transparent'
                            }
                        }
                    },
                    '& .MuiInputAdornment-root': {
                        color: 'var(--quaternary-color)',
                        padding: '0rem 0.3rem',
                        marginRight: '0rem'
                    },
                    '& .MuiAutocomplete-popupIndicator': {
                        color: 'var(--quaternary-color)',
                        '&.Mui-disabled': {
                            color: 'var(--quaternary-color)',
                            opacity: '0.5'
                        }
                    },
                    '& .MuiChip-root': {
                        backgroundColor: 'var(--secondary-color)',
                        fontWeight: '600',
                        color: 'var(--primary-color)',
                        fontSize: '1rem',
                        width: 'auto',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '5px 10px',
                        '& .MuiChip-deleteIcon': {
                            width: 'auto',
                            color: '#11203A !important',
                            fontSize: '1.175rem !important',
                            margin: '0px !important',
                            borderRadius: '50%',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                color: 'var(--quaternary-color)'
                            }
                        },
                        '& .MuiChip-label': {
                            color: 'var(--primary-color)',
                            padding: 0,
                            margin: 0
                        }
                    }
                }
            }
        },
        // MuiSvgIcon: {
        //     styleOverrides: {
        //         root: {
        //             color: 'var(--quaternary-color)',
        //             '&.Mui-disabled': {
        //                 color: 'var(--quaternary-color)',
        //                 opacity: '0.5',
        //             }
        //         },
        //     },
        // },
        MuiPaper: {
            styleOverrides: {
                root: {
                    fontFamily: 'var(--primary-heading-font-family)'
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontFamily: 'var(--primary-heading-font-family)',
                    fontWeight: '300',
                    fontSize: '1.125rem',
                    textTransform: 'none',
                    variants: [
                        {
                            props: { variant: 'text' },
                            style: {
                                color: 'var(--secondary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '400',
                                borderRadius: '1rem',
                                padding: '0.375rem 0.75rem',
                                backgroundColor: 'transparent',
                                letterSpacing: '0',
                                '&.Mui-disabled': {
                                    color: 'var(--primary-color)',
                                    opacity: '0.6',
                                    backgroundColor: 'var(--quaternary-color)',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                }
                            }
                        },
                        {
                            props: { variant: 'textSm' },
                            style: {
                                color: 'var(--secondary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1rem',
                                fontWeight: '500',
                                borderRadius: '0.875rem',
                                padding: '0.375rem 0.75rem',
                                backgroundColor: 'transparent',
                                letterSpacing: '0',
                                '&.Mui-disabled': {
                                    color: 'var(--primary-color)',
                                    opacity: '0.6',
                                    backgroundColor: 'var(--quaternary-color)',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                }
                            }
                        },
                        {
                            props: { variant: 'main' },
                            style: {
                                color: 'var(--quaternary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '500',
                                borderRadius: '1.25rem',
                                padding: '0.375rem 0.75rem',
                                minWidth: '80px',
                                backgroundColor: 'var(--secondary-color)',
                                letterSpacing: '0',
                                transition: 'all 0.3s ease',
                                '&.Mui-disabled': {
                                    color: 'var(--primary-color)',
                                    opacity: '0.6',
                                    backgroundColor: 'var(--quaternary-color)',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all',
                                    '&:hover': {
                                        backgroundColor: 'var(--quaternary-color)',
                                        color: 'var(--primary-color)'
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: 'var(--tertiary-color)',
                                    color: 'var(--primary-color)'
                                },
                                '@media (max-width: 480px)': {
                                    fontSize: '1rem',
                                    // padding: '0.35rem 0.6rem',
                                    borderWidth: '1.5px',
                                },
                                '@media (max-width: 768px)': {
                                    // padding: '0.3rem 0.5rem',
                                    borderRadius: '1rem',
                                    fontSize: '1rem',
                                },
                            }
                        },
                        {
                            props: { variant: 'mainSm' },
                            style: {
                                color: 'var(--quaternary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1rem',
                                fontWeight: '500',
                                borderRadius: '0.875rem',
                                padding: '0.375rem 0.75rem',
                                minWidth: '80px',
                                backgroundColor: 'var(--secondary-color)',
                                letterSpacing: '0',
                                lineHeight: 'auto',
                                transition: 'all 0.3s ease',
                                '&.Mui-disabled': {
                                    color: 'var(--primary-color)',
                                    opacity: '0.6',
                                    backgroundColor: 'var(--quaternary-color)',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all',
                                    '&:hover': {
                                        backgroundColor: 'var(--quaternary-color)',
                                        color: 'var(--primary-color)'
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: 'var(--tertiary-color)',
                                    color: 'var(--primary-color)'
                                },
                                '@media (max-width: 480px)': {
                                    fontSize: '1rem',
                                    // padding: '0.35rem 0.6rem',
                                    borderWidth: '1.5px',
                                },
                                '@media (max-width: 768px)': {
                                    // padding: '0.3rem 0.5rem',
                                    borderRadius: '1rem',
                                    fontSize: '1rem',
                                },
                            }
                        },
                        {
                            props: { variant: 'delete' },
                            style: {
                                color: 'var(--quaternary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '500',
                                borderRadius: '1.25rem',
                                border: `2px solid transparent`,
                                padding: '0.375rem 0.75rem',
                                minWidth: '80px',
                                backgroundColor: 'var(--error-main-color)',
                                letterSpacing: '0',
                                transition: 'all 0.3s ease',
                                '&.Mui-disabled': {
                                    color: 'var(--quaternary-color)',
                                    opacity: '0.6',
                                    backgroundColor: 'var(--error-main-color)',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                },
                                '&:hover': {
                                    color: 'var(--error-secondary-color)',
                                    backgroundColor: 'var(--error-light-bg-color)',
                                    borderColor: `var(--error-light-border-color)`
                                }
                            }
                        },
                        {
                            props: { variant: 'tertiaryMain' },
                            style: {
                                color: 'var(--quaternary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '500',
                                borderRadius: '1.25rem',
                                padding: '0.375rem 0.75rem',
                                minWidth: '80px',
                                backgroundColor: 'transparent',
                                letterSpacing: '0',
                                transition: 'all 0.3s ease',
                                border: '2px solid var(--tertiary-color)',
                                '&.Mui-disabled': {
                                    color: 'var(--quaternary-color)',
                                    opacity: '0.6',
                                    backgroundColor: 'transparent',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                },
                                '&:hover': {
                                    backgroundColor: 'transparent'
                                }
                            }
                        },
                        {
                            props: { variant: 'tertiary' },
                            style: {
                                color: 'var(--quaternary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '500',
                                borderRadius: '1rem',
                                padding: '0.375rem 0.75rem',
                                minWidth: '80px',
                                backgroundColor: 'var(--quinary-main-color)',
                                letterSpacing: '0',
                                transition: 'all 0.3s ease',
                                '&.Mui-disabled': {
                                    color: 'var(--primary-color)',
                                    opacity: '0.6',
                                    backgroundColor: 'var(--quaternary-color)',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                },
                                '&:hover': {
                                    backgroundColor: 'var(--tertiary-color)'
                                }
                            }
                        },
                        {
                            props: { variant: 'toggleActive' },
                            style: {
                                color: 'var(--quaternary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '400',
                                borderRadius: '1.25rem',
                                padding: '0.375rem 0.75rem',
                                backgroundColor: 'transparent',
                                border: '2px solid',
                                borderColor: 'var(--secondary-color)',
                                letterSpacing: '0',
                                '&.Mui-disabled': {
                                    color: 'var(--primary-color)',
                                    opacity: '0.6',
                                    backgroundColor: 'var(--quaternary-color)',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                },
                                '&:hover': {
                                    color: 'var(--tertiary-color)'
                                }
                            }
                        },
                        {
                            props: { variant: 'toggleTransparent' },
                            style: {
                                color: 'var(--quaternary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '400',
                                borderRadius: '1.25rem',
                                padding: '0.375rem 0.75rem',
                                backgroundColor: 'transparent',
                                border: '2px solid',
                                borderColor: 'var(--quaternary-color)',
                                letterSpacing: '0',
                                '&.Mui-disabled': {
                                    color: 'var(--primary-color)',
                                    opacity: '0.6',
                                    backgroundColor: 'var(--quaternary-color)',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                },
                                '&:hover': {
                                    borderColor: 'var(--tertiary-color)'
                                },
                                '@media (max-width: 480px)': {
                                    fontSize: '1rem',
                                    // padding: '0.35rem 0.6rem',
                                    borderWidth: '1.5px',
                                },
                                '@media (max-width: 768px)': {
                                    // padding: '0.3rem 0.5rem',
                                    borderRadius: '1rem',
                                    fontSize: '1rem',
                                },
                            }
                        },
                        {
                            props: { variant: 'publish' },
                            style: {
                                color: 'var(--primary-color)',
                                textTransform: 'capitalize',
                                fontSize: '1rem',
                                fontWeight: 400,
                                borderRadius: '1.25rem',
                                padding: '0.313rem 0.625rem',
                                maxHeight: '32px',
                                backgroundColor: 'var(--success-main-color)',
                                border: `1px solid transparent`,
                                '&.Mui-disabled': {
                                    color: 'var(--success-main-color)',
                                    backgroundColor: 'var(--success-light-bg-color-2)',
                                    borderColor: 'var(--success-main-color)',
                                    opacity: '0.5',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                }
                            }
                        },
                        {
                            props: { variant: 'pause' },
                            style: {
                                color: 'var(--primary-color)',
                                textTransform: 'capitalize',
                                fontSize: '1rem',
                                fontWeight: 400,
                                borderRadius: '1.25rem',
                                padding: '0.313rem 0.625rem',
                                maxHeight: '32px',
                                backgroundColor: 'var(--tertiary-color)',
                                border: `1px solid transparent`,
                                '&.Mui-disabled': {
                                    color: 'var(--tertiary-color)',
                                    backgroundColor: 'var(--tertiary-light-bg-color)',
                                    borderColor: 'var(--tertiary-color)',
                                    opacity: '0.5',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                }
                            }
                        },
                        {
                            props: { variant: 'warning' },
                            style: {
                                color: 'var(--secondary-color)',
                                textTransform: 'capitalize',
                                fontSize: '1rem',
                                fontWeight: 400,
                                borderRadius: '1.25rem',
                                padding: '0.313rem 0.625rem',
                                maxHeight: '32px',
                                backgroundColor: 'transparent',
                                border: `1px solid var(--secondary-color)`,
                                '&.Mui-disabled': {
                                    color: 'var(--secondary-color)',
                                    opacity: '0.5',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                }
                            }
                        },
                        {
                            props: { variant: 'success' },
                            style: {
                                color: 'var(--success-secondary-color)',
                                textTransform: 'capitalize',
                                fontSize: '1rem',
                                fontWeight: 400,
                                borderRadius: '1.25rem',
                                padding: '0.313rem 0.625rem',
                                maxHeight: '32px',
                                backgroundColor: 'transparent',
                                border: `1px solid var(--success-secondary-color)`,
                                '&.Mui-disabled': {
                                    color: 'var(--success-secondary-color)',
                                    backgroundColor: 'var(--success-light-bg-color-2)',
                                    borderColor: 'var(--success-secondary-color)',
                                    opacity: '0.5',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                }
                            }
                        },
                        {
                            props: { variant: 'cancel' },
                            style: {
                                color: 'var(--quaternary-color)',
                                textTransform: 'capitalize',
                                fontSize: '1rem',
                                fontWeight: 400,
                                borderRadius: '1.25rem',
                                padding: '0.313rem 0.625rem',
                                maxHeight: '32px',
                                backgroundColor: '#B3261E',
                                border: `1px solid transparent`,
                                '&.Mui-disabled': {
                                    color: 'var(--error-main-color)',
                                    backgroundColor: 'var(--error-light-bg-color-2)',
                                    borderColor: 'var(--error-main-color)',
                                    opacity: '0.5',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                }
                            }
                        },
                        {
                            props: { variant: 'markAsSoldOut' },
                            style: {
                                fontWeight: 400,
                                fontSize: '1rem',
                                maxHeight: 'auto',
                                padding: '0rem 0.75rem',
                                borderRadius: '0.875rem',
                                color: 'var(--error-secondary-color)',
                                backgroundColor: 'var(--error-light-bg-color)',
                                border: `1px solid var(--error-light-border-color)`,
                                '&.Mui-disabled': {
                                    color: 'var(--error-secondary-color)',
                                    backgroundColor: 'var(--error-light-bg-color-2)',
                                    borderColor: 'var(--error-secondary-color)',
                                    opacity: '0.5',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                }
                            }
                        },
                        {
                            props: { variant: 'reject' },
                            style: {
                                color: 'var(--error-main-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '400',
                                borderRadius: '1.25rem',
                                padding: '0.375rem 0.75rem',
                                backgroundColor: 'transparent',
                                border: 'none',
                                borderColor: 'var(--error-main-color)',
                                letterSpacing: '0',
                                '&.Mui-disabled': {
                                    color: 'var(--error-main-color)',
                                    opacity: '0.6',
                                    backgroundColor: 'transparent',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                },
                                '&:hover': {
                                    color: 'var(--error-main-color)'
                                },
                                '@media (max-width: 480px)': {
                                    fontSize: '1rem',
                                    // padding: '0.35rem 0.6rem',
                                    borderWidth: '1.5px',
                                },
                                '@media (max-width: 768px)': {
                                    // padding: '0.3rem 0.5rem',
                                    borderRadius: '1rem',
                                    fontSize: '1rem',
                                },
                            }
                        },
                        {
                            props: { variant: 'lightToggleActiveSm' },
                            style: {
                                color: 'var(--quaternary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1rem',
                                fontWeight: '400',
                                borderRadius: '1rem',
                                padding: '0.375rem 0.75rem',
                                backgroundColor: 'transparent',
                                border: '1px solid',
                                borderColor: 'var(--secondary-color)',
                                letterSpacing: '0',
                                transition: 'all 0.3s ease',
                                '&.Mui-disabled': {
                                    color: 'var(--primary-color)',
                                    opacity: '0.6',
                                    backgroundColor: 'var(--quaternary-color)',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                },
                                '&:hover': {
                                    color: 'var(--secondary-color)'
                                }
                            }
                        },
                    ]
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    fontFamily: 'var(--primary-heading-font-family)',
                    fontWeight: '300',
                    fontSize: '1.125rem',
                    textTransform: 'none',
                    color: 'var(--quaternary-color)',
                    variants: [
                        {
                            props: { color: 'primary' },
                            style: {
                                color: 'var(--quaternary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '400',
                                borderRadius: '1.25rem'
                            }
                        },
                        {
                            props: { color: 'secondary' },
                            style: {
                                color: 'var(--secondary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '400',
                                borderRadius: '1.25rem'
                            }
                        },
                        {
                            props: { color: 'tertiary' },
                            style: {
                                color: 'var(--tertiary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '400',
                                borderRadius: '1.25rem'
                            }
                        },
                        {
                            props: { color: 'elipsis' },
                            style: {
                                color: 'var(--quaternary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.25rem',
                                fontWeight: '400',
                                borderRadius: '1.25rem',
                                width: '2rem',
                                height: '2rem'
                            }
                        },
                        {
                            props: { color: 'danger' },
                            style: {
                                color: 'var(--error-main-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '400',
                                borderRadius: '1.25rem'
                            }
                        },
                        {
                            props: { color: 'lightSecondary' },
                            style: {
                                color: 'var(--secondary-color)',
                                fontFamily: 'var(--primary-heading-font-family)',
                                fontSize: '1.125rem',
                                fontWeight: '400',
                                borderRadius: '0.75rem',
                                backgroundColor: 'var(--warning-light-bg-color)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: 'var(--secondary-color)',
                                    color: 'var(--primary-color)',
                                }
                            }
                        }
                    ]
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontFamily: 'var(--primary-heading-font-family)',
                    fontWeight: '500',
                    fontSize: '2rem',
                    color: 'var(--secondary-color)'
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    opacity: 1,
                    color: 'var(--tertiary-color)',
                    fontSize: '1rem',
                    minWidth: '236px',
                    borderRadius: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#3e414d',
                    textTransform: 'capitalize',
                    border: `1px solid ${'var(--tertiary-color)'}`,
                    fontFamily: 'var(--primary-heading-font-family)',
                    variants: [
                        {
                            props: { variant: 'main' },
                            style: {
                                backgroundColor: '#3a414d'
                            }
                        },
                        {
                            props: { variant: 'inactive' },
                            style: {
                                backgroundColor: '#3a414d',
                                border: `1px solid transparent`,
                                color: '#f5f5f5'
                            }
                        },
                        {
                            props: { variant: 'disabled' },
                            style: {
                                // backgroundColor: 'var(--quaternary-color)',
                                color: 'var(--quaternary-color)',
                                opacity: '0.5',
                                border: `1px solid transparent`,
                                cursor: 'not-allowed',
                                pointerEvents: 'none'
                            }
                        }
                    ]
                }
            }
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: '0.625rem',
                    color: 'var(--quaternary-color)',
                    mb: 0.5,
                    '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    variants: [
                        {
                            props: { variant: 'dashboardListItem' },
                            style: {
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                                    backdropFilter: 'blur(4px)'
                                },
                                '&.Mui-selected': {
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                                    backdropFilter: 'blur(4px)',
                                    color: 'var(--secondary-color)'
                                },
                                '&.Mui-selected:hover': {
                                    background: 'rgba(255, 255, 255, 0.2)'
                                }
                            }
                        },
                        {
                            props: { variant: 'dashboardListCollapse' },
                            style: {
                                paddingLeft: '3rem',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    color: 'var(--secondary-color)',
                                    background: 'transparent !important'
                                },
                                '&.Mui-selected': {
                                    color: 'var(--secondary-color)',
                                    background: 'transparent'
                                },
                                '&.Mui-selected:hover': {
                                    background: 'transparent'
                                }
                            }
                        }
                    ]
                }
            }
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: '40px',
                    color: 'var(--quaternary-color)'
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                root: {
                    '& .MuiTypography-root': {
                        fontFamily: 'var(--secondary-content-font-family)'
                    }
                }
            }
        },
        MuiIcon: {
            styleOverrides: {
                root: {
                    variants: [
                        {
                            props: { color: 'secondary' },
                            style: {
                                color: 'var(--secondary-color)'
                            }
                        },
                        {
                            props: { color: 'tertiary' },
                            style: {
                                color: 'var(--tertiary-color)'
                            }
                        },
                        {
                            props: { color: 'white' },
                            style: {
                                color: 'var(--quaternary-color)'
                            }
                        },
                        {
                            props: { color: 'error' },
                            style: {
                                color: 'var(--error-main-color)'
                            }
                        },
                        {
                            props: { color: 'success' },
                            style: {
                                color: 'var(--success-secondary-color)'
                            }
                        },
                        {
                            props: { color: 'fourth' },
                            style: {
                                color: 'rgb(59, 130, 246)'
                            }
                        }
                    ]
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontFamily: 'var(--primary-heading-font-family)',
                    variants: [
                        {
                            props: { variant: 'deletable' },
                            style: {
                                backgroundColor: 'var(--secondary-color)',
                                fontWeight: '600',
                                color: 'var(--primary-color)',
                                fontSize: '1rem',
                                width: 'auto',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '5px 10px',
                                '& .MuiChip-deleteIcon': {
                                    width: 'auto',
                                    color: '#11203A !important',
                                    fontSize: '1.175rem !important',
                                    margin: '0px !important',
                                    borderRadius: '50%',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        color: 'var(--quaternary-color)'
                                    }
                                },
                                '& .MuiChip-label': {
                                    color: 'var(--primary-color)',
                                    padding: 0,
                                    margin: 0
                                }
                            }
                        },
                        {
                            props: { variant: 'live' },
                            style: {
                                width: 'auto',
                                height: 'auto',
                                fontSize: '1rem',
                                fontWeight: '700',
                                padding: '0.15rem 0.75rem',
                                color: 'var(--success-secondary-color)',
                                backgroundColor: 'var(--success-light-bg-color)',
                                border: `1px solid var(--success-light-border-color)`,
                                '& .MuiChip-label': {
                                    padding: 0,
                                    margin: 0
                                }
                            }
                        },
                        {
                            props: { variant: 'scheduled' },
                            style: {
                                width: 'auto',
                                height: 'auto',
                                fontSize: '1rem',
                                fontWeight: '700',
                                padding: '0.15rem 0.75rem',
                                color: palette.fourth.secondary,
                                backgroundColor: 'var(--quinary-light-bg-color-2)',
                                border: `1px solid var(--quinary-secondary-color)`,
                                '& .MuiChip-label': {
                                    padding: 0,
                                    margin: 0
                                }
                            }
                        },
                        {
                            props: { variant: 'soldOut' },
                            style: {
                                width: 'auto',
                                height: 'auto',
                                fontSize: '1rem',
                                fontWeight: '700',
                                padding: '0.15rem 0.75rem',
                                color: 'var(--error-secondary-color)',
                                backgroundColor: 'var(--error-light-bg-color)',
                                border: `1px solid ${'var(--error-light-border-color)'}`,
                                '& .MuiChip-label': {
                                    padding: 0,
                                    margin: 0
                                }
                            }
                        },
                        {
                            props: { color: 'warning' },
                            style: {
                                color: 'var(--secondary-color)',
                                fontFamily: 'var(--secondary-content-font-family)',
                                backgroundColor: 'transparent',
                                border: `1px solid var(--secondary-color)`
                            }
                        },
                        {
                            props: { color: 'danger' },
                            style: {
                                color: 'var(--error-main-color)',
                                fontFamily: 'var(--secondary-content-font-family)',
                                backgroundColor: 'transparent',
                                border: `1px solid var(--error-main-color)`
                            }
                        },
                        {
                            props: { color: 'success' },
                            style: {
                                color: 'var(--success-secondary-color)',
                                fontFamily: 'var(--secondary-content-font-family)',
                                backgroundColor: 'transparent',
                                border: `1px solid var(--success-secondary-color)`
                            }
                        },
                        {
                            props: { color: 'normal' },
                            style: {
                                border: '1px solid var(--quaternary-color)',
                                padding: '0px 14px',
                                minHeight: '24px',
                                maxHeight: '44px',
                                borderRadius: '1.5rem',
                                boxSizing: 'border-box',
                                textTransform: 'capitalize',
                                backgroundColor: 'transparent',
                                '& .MuiChip-label': {
                                    padding: '0px'
                                }
                            }
                        },
                        {
                            props: { color: 'successSm' },
                            style: {
                                border: 'none',
                                padding: '0px 14px',
                                minHeight: '24px',
                                maxHeight: '44px',
                                borderRadius: '0.75rem',
                                boxSizing: 'border-box',
                                textTransform: 'capitalize',
                                backgroundColor: 'var(--success-light-bg-color-2)',
                                maxWidth: 'fit-content',
                                '& .MuiChip-label': {
                                    padding: '0px',
                                    color: 'var(--success-secondary-color)',
                                    fontSize: '1rem',
                                    fontWeight: 500
                                }
                            }
                        },
                        {
                            props: { color: 'warningSm' },
                            style: {
                                border: 'none',
                                padding: '0px 14px',
                                minHeight: '24px',
                                maxHeight: '44px',
                                borderRadius: '0.75rem',
                                boxSizing: 'border-box',
                                textTransform: 'capitalize',
                                backgroundColor: 'var(--warning-light-bg-color)',
                                maxWidth: 'fit-content',
                                '& .MuiChip-label': {
                                    padding: '0px',
                                    color: 'var(--secondary-color)',
                                    fontSize: '1rem',
                                    fontWeight: 500
                                }
                            }
                        },
                        {
                            props: { color: 'errorSm' },
                            style: {
                                border: 'none',
                                padding: '0px 14px',
                                minHeight: '24px',
                                maxHeight: '44px',
                                borderRadius: '0.75rem',
                                boxSizing: 'border-box',
                                textTransform: 'capitalize',
                                backgroundColor: 'var(--error-light-bg-color-2)',
                                maxWidth: 'fit-content',
                                '& .MuiChip-label': {
                                    padding: '0px',
                                    color: 'var(--error-main-color)',
                                    fontSize: '1rem',
                                    fontWeight: 500
                                }
                            }
                        },
                        {
                            props: { color: 'scheduledSm' },
                            style: {
                                border: 'none',
                                padding: '0px 14px',
                                minHeight: '24px',
                                maxHeight: '44px',
                                borderRadius: '0.75rem',
                                boxSizing: 'border-box',
                                textTransform: 'capitalize',
                                backgroundColor: 'var(--tertiary-light-bg-color)',
                                maxWidth: 'fit-content',
                                '& .MuiChip-label': {
                                    padding: '0px',
                                    color: 'var(--tertiary-color)',
                                    fontSize: '1rem',
                                    fontWeight: 500
                                }
                            }
                        }
                    ]
                }
            }
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    fontFamily: 'var(--primary-heading-font-family)',
                    variants: [
                        {
                            props: { color: 'main' },
                            style: {
                                '& .MuiSwitch-switchBase': {
                                    color: 'var(--secondary-color)',
                                    padding: '9px'
                                },
                                '& .Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: 'var(--secondary-color)',
                                    color: 'var(--secondary-color)'
                                },
                                '& .Mui-disabled': {
                                    color: 'var(--quaternary-color)',
                                    '&.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: 'var(--quaternary-color)',
                                        color: 'var(--quaternary-color)'
                                    }
                                },
                                '& .MuiSwitch-track': {
                                    backgroundColor: 'var(--quaternary-color)'
                                },
                                '&.MuiSwitch-root': {
                                    width: '60px !important'
                                }
                            }
                        }
                    ]
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    margin: '0px',
                    '& .MuiFormControlLabel-label': {
                        color: 'var(--quaternary-color)',
                        fontFamily: 'var(--primary-heading-font-family)',
                        '&.Mui-disabled': {
                            color: 'var(--quaternary-color)'
                        }
                    }
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                root: {
                    backdropFilter: 'blur(4px)',
                    backgroundColor: 'transparent',
                    '& .MuiDrawer-paper': {
                        // backgroundColor: 'var(--primary-color)',
                        background: 'linear-gradient(to top left, #0A1528, #1D3A6E)',
                        color: 'var(--quaternary-color)',
                        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                        width: '460px',
                        height: '95.5vh',
                        top: '16px',
                        right: '14px',
                        bottom: '14px',
                        borderRadius: '1rem'
                    },
                    '& .MuiBackdrop-root': {
                        // backgroundColor: 'transparent'
                    }
                }
            }
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    '& .MuiTypography-root': {
                        fontFamily: 'var(--primary-heading-font-family)',
                        color: 'var(--quaternary-color)'
                    }
                }
            }
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    gap: '1rem',
                    variants: [
                        {
                            props: { color: 'main' },
                            style: {
                                '& .MuiToggleButton-root': {
                                    fontFamily: 'var(--secondary-content-font-family)',
                                    fontSize: '1.125rem',
                                    fontWeight: '400',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    textTransform: 'none',
                                    borderRadius: '1rem',
                                    padding: '0.5rem 0.875rem',
                                    color: 'var(--quaternary-color)',
                                    transition: 'all 0.3s ease',
                                    border: `2px solid transparent`,
                                    backgroundColor: 'var(--black-light-bg-color)',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    position: 'relative',
                                    '&:hover': {
                                        backgroundColor: 'var(--black-light-bg-color)',
                                        color: 'var(--quaternary-color)',
                                        borderColor: 'var(--secondary-color)'
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: 'var(--black-light-bg-color)',
                                        color: 'var(--quaternary-color)',
                                        borderColor: 'var(--secondary-color)'
                                    },
                                    '&.Mui-disabled': {
                                        color: 'var(--primary-color)',
                                        opacity: '0.5',
                                        background: 'var(--quaternary-color)'
                                    }
                                }
                            }
                        },
                        {
                            props: { color: 'secondary' },
                            style: {
                                '& .MuiToggleButton-root': {
                                    fontFamily: 'var(--secondary-content-font-family)',
                                    fontSize: '1rem',
                                    fontWeight: '400',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    textTransform: 'none',
                                    borderRadius: '1rem',
                                    padding: '0.325rem 1rem',
                                    color: 'var(--quaternary-color)',
                                    transition: 'all 0.3s ease',
                                    border: `1px solid var(--quaternary-color)`,
                                    backgroundColor: 'transparent',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    position: 'relative',
                                    '&.Mui-selected': {
                                        backgroundColor: 'var(--secondary-color)',
                                        color: 'var(--quaternary-color)',
                                        borderColor: 'var(--secondary-color)'
                                    },
                                    '&.Mui-disabled': {
                                        color: 'var(--primary-color)',
                                        opacity: '0.5',
                                        background: 'var(--secondary-color)'
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: 'var(--secondary-color)'
                                    }
                                }
                            }
                        }
                    ]
                }
            }
        },
        // MuiDialog: {
        //     styleOverrides: {
        //         root: {
        //             variants: [
        //                 {
        //                     props: { variant: 'login' },
        //                     style: {
        //                         '& .MuiPaper-root': {
        //                             width: '475px',
        //                             // height: '555px',
        //                             padding: '2rem',
        //                             display: 'flex',
        //                             border: 'none',
        //                             borderRadius: '1rem',
        //                             background: 'var(--gradient-blue)',
        //                             scrollbarWidth: 'none',
        //                         }
        //                     }
        //                 }
        //             ]
        //         }
        //     }
        // },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    variants: [
                        {
                            props: { color: 'secondary' },
                            style: {
                                color: 'var(--quaternary-color)',
                                padding: '5px',
                                '&.Mui-checked': {
                                    color: 'var(--secondary-color)'
                                }
                            }
                        }
                    ]
                }
            }
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: 'var(--secondary-color)',
                    '&.Mui-checked': {
                        color: 'var(--secondary-color)'
                    },
                    '&.Mui-disabled': {
                        color: 'var(--quaternary-color)',
                        opacity: '0.5'
                    }
                }
            }
        }
    },
    palette: palette
});
